import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Card, CardBody } from "reactstrap";
import { useHistory } from "react-router";
import { Backdrop, Checkbox, FormControlLabel } from "@mui/material";

import { ReactComponent as BackIcon } from "assets/img/svg/BackButton.svg";
import { ReactComponent as ExcellIcon } from "assets/img/svg/excel.svg";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import PaymentApprovalFilters from "./PaymentApprovalFilters";
import ALoader from "views/pages/manage/common/a-loader";

import PaymentApprovalTable from "./PaymentApprovalTable";
import {
  downloadVendorsRequestsCsv,
  fetchPaymentApprovalList,
  FILTERS_DEFAULT_STATE,
} from "./utils";

const PaymentApproval = ({
  showApproverActions = true,
  handleRaisePaymentRequest,
}) => {
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    ...FILTERS_DEFAULT_STATE,
    searchBy: "",
    searchByValue: "",
  });

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("vendorName");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  const masterDropdowns = useSelector(
    (state) => state.globalData.masterDropdowns,
  );

  const [buOptions, setBuOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);

  useEffect(() => {
    if (masterDropdowns?.businessArea?.length) {
      const options = masterDropdowns.businessArea.map((item) => ({
        label: item.label,
        value: item.businessAreaId,
      }));
      setBuOptions(options);

      const buPlant = masterDropdowns.businessArea.map((item) => ({
        label: item.sapPlantId,
        value: item.sapPlantId,
      }));
      setPlantOptions(buPlant);

      fetchTableData();
    }
  }, [masterDropdowns?.businessArea, filters, page, pageSize]);

  const fetchTableData = async (offset = page) => {
    setLoading(true);
    try {
      const { data } = await fetchPaymentApprovalList({
        searchBy: filters?.searchBy,
        searchByValue: filters?.searchByValue,
        filters,
        offset,
        limit: pageSize,
        businessAreaIds: masterDropdowns.businessArea.map(
          ({ businessAreaId }) => businessAreaId,
        ),
      });

      const list = data?.vendorTransactionDetailList || [];

      setRows(
        list.map((item) => ({
          ...item,
          paymentOrderId: item.id,
          panNumber: item.panNumber,
          vendorType: item.vendorType,
          vendorSapCode: item.vendorSapCode,
          sapPlantId: item.sapPlantId,
          vendorName: item.vendorName,
          businessAreaDispValue: item.businessAreaDispValue,
          jobDetails: item.designation,
          academicYearDispValue: item.academicYearDispValue,
          totalAmount: item.totalAmount,
          // createdOnBy: item.createdOnBy,
          updatedOn: item.updatedOn,
          status: item.approvalStatus,
        })),
      );

      setTotalRows(data?.totalRecords || 0);
    } catch (e) {
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOnUserInput = (value) => {
    setSearch(value);
    if (value === "" && value !== filters.searchByValue) handleSearch(value);
  };

  const handleSearch = (value = search) => {
    setPage(0);

    setFilters((prev) => ({
      ...prev,
      searchBy: value ? selected : "",
      searchByValue: value,
    }));
  };

  const selectOnlyPending = (checked) => {
    setPage(0);

    setFilters((prev) => ({
      ...prev,
      status: checked
        ? [
            "SUBMITTED",
            "L1_APPROVED",
            "L2_APPROVED",
            "L3_APPROVED",
            "L4_APPROVED",
          ]
        : [],
    }));
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      await downloadVendorsRequestsCsv({
        searchBy: filters?.searchBy,
        searchByValue: filters?.searchByValue,
        filters,
      });
    } catch (error) {
      console.error("Error during CSV export:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexFlow: "column",
        height: "calc(100vh - 100px)",
      }}
    >
      <Card className="mb-2 mx-0" style={{ flexGrow: 1 }}>
        <CardBody style={{ display: "flex", flexFlow: "column" }}>
          <div className="search-match-container d-flex justify-content-between mb-3">
            <div
              className="d-flex align-items-center"
              style={{ height: "min-content" }}
            >
              <BackIcon
                className="mr-2 large-text"
                onClick={() => history.goBack()}
              />
              <div className="heading-3 color-black-60 text-bold">
                Vendors requests
              </div>
            </div>

            <div className="d-flex justify-content-center align-items-center">
              <FormControlLabel
                sx={{
                  "& .MuiFormControlLabel-label": { fontSize: "12px" },
                }}
                control={
                  <Checkbox
                    onChange={(e) => selectOnlyPending(e.target.checked)}
                    checked={
                      filters?.status?.filter((option) =>
                        [
                          "SUBMITTED",
                          "L1_APPROVED",
                          "L2_APPROVED",
                          "L3_APPROVED",
                          "L4_APPROVED",
                        ].includes(option),
                      ).length === 5
                    }
                  />
                }
                label="Pending approval"
              />

              <AInput
                placeholder="Search"
                value={search}
                handler={handleOnUserInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                searchIconClickHandler={() => handleSearch()}
                crossIcon={false}
                searchIcon={true}
                style={{ width: "300px" }}
                rightCustomIcon={
                  <select
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    style={{
                      position: "absolute",
                      bottom: "0",
                      margin: "auto 0",
                      background: "#FFF6E3",
                      color: "#A17217",
                      border: "none",
                    }}
                  >
                    <option value="vendorName">Vendor Name</option>
                    <option value="panNumber">PAN</option>
                    <option value="vendorSapCode">SAP Vendor ID</option>
                    <option value="paymentOrderId">Payment Order ID</option>
                  </select>
                }
                rightCustomIconStyles={{
                  fontSize: "12px",
                  position: "absolute",
                  bottom: "7px",
                  right: "120px",
                  margin: "auto",
                }}
              />

              <PaymentApprovalFilters
                onApply={(filterForm) => {
                  setPage(0);
                  setFilters((prev) => ({
                    ...prev,
                    ...filterForm,
                  }));
                }}
                onReset={() => {
                  setPage(0);
                  setFilters({
                    ...FILTERS_DEFAULT_STATE,
                    searchBy: "",
                    searchByValue: "",
                  });
                  setSearch("");
                }}
                buOptions={buOptions}
                plantOptions={plantOptions}
                isLoading={loading}
                filters={filters}
              />

              <AButton onClick={handleExportCSV} className="ml-2">
                <ExcellIcon /> Export CSV
              </AButton>

              {!showApproverActions && (
                <AButton
                  variant="primary_filled"
                  size="xs"
                  onClick={handleRaisePaymentRequest}
                >
                  Raise a new request
                </AButton>
              )}
            </div>
          </div>

          <div style={{ height: "100%" }}>
            <PaymentApprovalTable
              rows={rows}
              loading={loading}
              setLoading={setLoading}
              rowCount={totalRows}
              showApproverActions={showApproverActions}
              fetchTableData={fetchTableData}
              pagination={{
                page: page + 1,
                pageSize: pageSize,
                total: totalRows,
                totalPages: Math.ceil(totalRows / pageSize) || 1,
              }}
              onPageChange={(newPage) => setPage(newPage - 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
            />
          </div>
        </CardBody>
      </Card>

      {loading && (
        <Backdrop
          sx={(theme) => ({
            // color: "#fff",
            zIndex: 2200,
          })}
          open={loading}
        >
          <ALoader />
        </Backdrop>
      )}
    </div>
  );
};

export default PaymentApproval;
